/**
 * What a manager reads the morning after.
 *
 * Three things, in the order they need acting on:
 *
 *   1. THE CHECKS NOBODY CLOSED. First, and deliberately without a
 *      "close them all" button. Sweeping an unsigned check onto a
 *      member's account charges them for something nobody agreed to,
 *      and the club finds out when the member does. Each one is opened,
 *      looked at, and charged or voided by somebody who can decide.
 *   2. THE SERVICE. Checks, covers and what posted, by counter and day.
 *   3. WHAT WAS GIVEN AWAY. Comps never appear in revenue — that is what
 *      a comp is — so the only place they can be seen is a list of them,
 *      with who authorized each one. A club that cannot see this number
 *      finds it in food cost at year end and cannot explain it.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { date as fmtDate } from '../lib/format.ts';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';
import { Face } from '../components/Face.tsx';

interface StaleTab {
  tab_id: string;
  outlet_name: string;
  business_date: string;
  tab_number: number;
  label: string | null;
  account_name: string | null;
  account_photo_uri: string | null;
  line_count: number;
  unposted_minor: number;
  days_open: number;
}

interface SummaryRow {
  outlet_id: string;
  outlet_name: string;
  business_date: string;
  checks: number;
  covers: number | null;
  posted_minor: number;
  still_open_minor: number;
  comped_minor: number;
  voided_minor: number;
}

interface Comp {
  id: string;
  description: string;
  comp_reason: string;
  comped_at: string;
  extended_minor: number;
  tab_number: number;
  outlet_name: string;
  authorized_by: string | null;
}

export function FnbServicePage() {
  const { currency } = useSession();
  const [days, setDays] = useState(14);

  const from = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);

  const stale = useApi<{ tabs: StaleTab[] }>('/v1/fnb/stale-tabs');
  const service = useApi<{ summary: SummaryRow[]; comps: Comp[] }>(
    `/v1/fnb/service-summary?from=${from}`,
    [days],
  );

  const rows = service.data?.summary ?? [];
  const totals = rows.reduce(
    (acc, r) => ({
      checks: acc.checks + r.checks,
      covers: acc.covers + (r.covers ?? 0),
      posted: acc.posted + r.posted_minor,
      comped: acc.comped + r.comped_minor,
      voided: acc.voided + r.voided_minor,
    }),
    { checks: 0, covers: 0, posted: 0, comped: 0, voided: 0 },
  );

  return (
    <>
      <h1 style={{ marginBottom: 16 }}>Food &amp; Beverage Service</h1>
      <ErrorBanner error={stale.error} />
      <ErrorBanner error={service.error} />

      <Card title="Checks Nobody Closed">
        {stale.loading ? (
          <Loading rows={2} />
        ) : (stale.data?.tabs.length ?? 0) === 0 ? (
          <Empty>Every check from an earlier day was closed. Nothing to work.</Empty>
        ) : (
          <>
            <table className="data">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Counter</th>
                  <th style={{ width: 56 }}>#</th>
                  <th>Check</th>
                  <th>Signing</th>
                  <th className="num">On it</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(stale.data?.tabs ?? []).map((t) => (
                  <tr key={t.tab_id}>
                    <td>
                      {fmtDate(t.business_date)}
                      <div className="muted small">
                        {t.days_open} day{t.days_open === 1 ? '' : 's'} open
                      </div>
                    </td>
                    <td>{t.outlet_name}</td>
                    <td className="num mono">{t.tab_number}</td>
                    <td>{t.label ?? '—'}</td>
                    <td>
                      {/* The face, because working this list is ringing
                          somebody up about a check they walked away
                          from, and the manager wants to know who. */}
                      <span
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <Face
                          name={t.account_name}
                          photoUri={t.account_photo_uri}
                          size={28}
                        />
                        {t.account_name ?? <Status tone="warning">nobody named</Status>}
                      </span>
                    </td>
                    <td className="num">
                      <Money minor={t.unposted_minor} currency={currency} />
                    </td>
                    <td>
                      <Link className="secondary small" to="/fnb/terminal">
                        Open the terminal
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="hint" style={{ marginTop: 10 }}>
              There is no button here that charges these in bulk, on purpose. A check nobody
              signed, posted automatically, is a member charged for something they did not
              agree to — and the club hears about it from the member.
            </div>
          </>
        )}
      </Card>

      <div className="row" style={{ margin: '16px 0', alignItems: 'flex-end', gap: 12 }}>
        <label className="field" style={{ maxWidth: 160 }}>
          <span>Look back</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </label>
      </div>

      <div className="tiles">
        <Tile label="Checks" value={totals.checks} />
        <Tile label="Covers" value={totals.covers} />
        <Tile
          label="Charged"
          value={<Money minor={totals.posted} currency={currency} />}
        />
        <Tile
          label="Given away"
          value={<Money minor={totals.comped} currency={currency} />}
          note={totals.comped > 0 ? 'never appears in revenue' : undefined}
          tone={totals.comped > 0 ? 'warning' : undefined}
        />
        <Tile label="Struck" value={<Money minor={totals.voided} currency={currency} />} />
      </div>

      <Card title="By Counter and Day">
        {service.loading ? (
          <Loading rows={5} />
        ) : rows.length === 0 ? (
          <Empty>No service recorded in this window.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Day</th>
                <th>Counter</th>
                <th className="num">Checks</th>
                <th className="num">Covers</th>
                <th className="num">Charged</th>
                <th className="num">Still open</th>
                <th className="num">Comped</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.business_date}-${r.outlet_id}`}>
                  <td>{fmtDate(r.business_date)}</td>
                  <td>{r.outlet_name}</td>
                  <td className="num">{r.checks}</td>
                  <td className="num">{r.covers ?? '—'}</td>
                  <td className="num">
                    <Money minor={r.posted_minor} currency={currency} />
                  </td>
                  <td className="num">
                    {r.still_open_minor > 0 ? (
                      <Money minor={r.still_open_minor} currency={currency} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="num">
                    {r.comped_minor > 0 ? (
                      <Money minor={r.comped_minor} currency={currency} />
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="What Was Given Away">
        {(service.data?.comps.length ?? 0) === 0 ? (
          <Empty>Nothing was comped in this window.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Item</th>
                <th>Counter</th>
                <th style={{ width: 56 }}>Check</th>
                <th>Why</th>
                <th>Authorized by</th>
                <th className="num">Worth</th>
              </tr>
            </thead>
            <tbody>
              {(service.data?.comps ?? []).map((c) => (
                <tr key={c.id}>
                  <td>{c.description}</td>
                  <td>{c.outlet_name}</td>
                  <td className="num mono">{c.tab_number}</td>
                  <td>{c.comp_reason}</td>
                  <td>{c.authorized_by ?? <span className="muted">—</span>}</td>
                  <td className="num">
                    <Money minor={c.extended_minor} currency={currency} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
